import React, { useState, useEffect, useMemo } from "react";
import Grid from "utils/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Autocomplete from '@mui/material/Autocomplete';
import { connect } from "react-redux";
import moment from "dayjs";
import _ from "lodash";
import { useSelector } from "react-redux";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import GetAppIcon from "@mui/icons-material/GetApp";
import TableGenerator from "../common/CollapsibleTableGenerator";
import { BootstrapTooltip } from "./BootstrapTooltip";
import DownloadButton, {
  ButtonIcon,
  ApplyButton,
} from "components/Common/Buttons";
import AsyncAutoComplete from "../common/AsyncAutoComplete";
import CustomInputDatePicker from "../../../../../Common/CustomInputDatePicker";
import {
  convertUtcToLocal,
  dateFormatter,
  getFilterEndDate,
  getFilterStartDate,
  getTimeZoneDifference,
  currencyFormatter,
} from "utils/helpers";
import { downloadBlob } from "helpers";
import styles from "./styles";
import SearchBox from "../common/SearchBox";

function Orders() {
  const classes = styles();
  const searchFilterFields = ["card_number", "order_id", "transaction_id"];
  const operatorsData = useSelector(
    (state) => state.dropdownReducer.operatorsData
  );
  const areasData = useSelector((state) => state.dropdownReducer.areasData);
  const locationsData = useSelector(
    (state) => state.dropdownReducer.locationsData
  );
  const vmData = useSelector((state) => state.dropdownReducer.devicesData);
  const orderStatusData = useSelector(
    (state) => state.dropdownReducer.orderStatusData
  );
  const productsData = useSelector(
    (state) => state.dropdownReducer.productsData
  );

  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(
    moment().subtract(15, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());

  const [locationList, setLocationList] = useState(locationsData || []);
  const [areaList, setAreaList] = useState(areasData || []);
  const [vmList, setVmList] = useState(vmData || []);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [more, setMore] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [limit, setLimit] = useState(25);
  const [loader, setLoader] = useState(false);
  const [fetchDataParams, setFetchDataParams] = useState({});
  const [ordering, setOrdering] = useState("-order_date");
  const [selectedField, setSelectedField] = useState(null);
  const [filters, setFilters] = useState({
    operator: { value: "all", label: "All" },
    area: { value: "all", label: "All" },
    location: { value: "all", label: "All" },
    vm: { value: "all", label: "All" },
    product: { value: "all", label: "All" },
    status: [{ value: "all", label: "All" }],
  });
  const [areaListLoading, setAreaListLoading] = useState(false);
  const [locationListLoading, setLocationListLoading] = useState(false);
  const [vmListLoading, setVmListLoading] = useState(false);
  const [filterable, setFilterable] = useState("card_number");

  const enableTaxesPerVM = useSelector(
    (state) => state.companySettingsReducer.enableTaxesPerVM
  );

  const downloadPDF = (order_id) => {
    window.open(`/receipts/${order_id}`, "_blank");
  };

  function snakeToTitle(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const setup = () => {
    setLoader(true);
    setOrders([]);
    setModulesSelected([]);
  };

  const handleRes = (data = []) => {
    setOrders(data?.results);
    setLoader(false);
    setNextPage(data.next);
    setPreviousPage(data.previous);
  };

  const changePage = (url) => {
    setup();
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        handleRes(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const collapsibleTableFields = [
    {
      key: "product_id",
      columnName: "Product Id",
    },
    {
      key: "product_sku",
      columnName: "Product SKU",
      render: (val) =>
        val?.product_sku
          ? val?.product_sku
          : _.find(productsData, (x) => x.id == val.product_id)?.product_sku ||
            "---",
    },
    {
      key: "product_name",
      columnName: "Product Name",
    },
    {
      key: "module_number",
      columnName: "Cabinet",
      render: (values) => {
        return values?.module_number
          ? `Module ${values.module_number?.split("-")[1]}`
          : "1";
      },
    },
    {
      key: "rack_no",
      columnName: "Rack",
      render: (values) => {
        if (values.row === -1 || values.col === -1) {
          return "---";
        }
        return `${values.row}*${values.col}`;
      },
    },
    {
      key: "quantity",
      columnName: "Quantity",
    },
    {
      key: "product_price",
      columnName: "Price ($)",
      render: (value) => currencyFormatter(value.product_price) ?? "---",
    },
    {
      key: "product_status",
      columnName: "Dispense Status",
    },
    {
      key: "total_amount",
      columnName: "Total Amount ($)",
      render: (value) => currencyFormatter(value.total_amount) ?? "---",
    },
    {
      key: "deals",
      columnName: "Discount Codes",
      render: (value) =>
        value?.deals?.length > 0
          ? value?.deals.map((x) => x.discount_code)?.join(", ")
          : "---",
    },
    {
      key: "metrc_tag_id",
      columnName: "Metrc Tag",
      render: (value) => value.metrc_tag_id ?? "---",
    },
  ];

  const collapsibleTableFields2 = [
    {
      key: "status",
      columnName: "Order Status",
      render: (value) => value.status ?? "---",
    },
    {
      key: "order_date",
      columnName: "Date and Time",
      render: (value) =>
        dateFormatter(convertUtcToLocal(value.order_date), true) ?? "---",
    },
  ];

  const collapsibleTableFields3 = [
    {
      key: "product_cost",
      columnName: "MRP ($)",
      visible: true,
      render: (value) =>
        value?.product_cost || value?.product_cost == 0
          ? currencyFormatter(value.product_cost)
          : "---",
    },
    {
      key: "total_discount",
      columnName: "Discount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.total_discount) ?? "---",
    },
    {
      key: "order_amount",
      columnName: "Order Amount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.order_amount) ?? "---",
    },
    {
      key: "tax",
      columnName: "Additional Tax ($)",
      visible: enableTaxesPerVM,
      render: (value) => currencyFormatter(value.tax) ?? "---",
    },
    {
      key: "tax_per_vm",
      columnName: "Tax ($)",
      visible: true,
      render: (value) => currencyFormatter(value.tax_per_vm) ?? "---",
    },
    {
      key: "total_amount",
      columnName: "Total Amount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.total_amount) ?? "---",
    },
    {
      key: "error_message",
      columnName: "Error Message",
      visible: true,
      render: (value) => value.error_message ?? "---",
    },
  ];

  const productById = useMemo(() => {
    const m = new Map();
    productsData.forEach((p) => {
      m.set(String(p.value), p.label);
      if (p.product_sku) m.set(String(p.product_sku), p.label);
    });
    return m;
  }, [productsData]);

  const renderOrderItems = (items) => {
    if (!items || items.length === 0) return "---";

    const selected = filters?.product?.value;

    if (!selected || String(selected) === "all") {
      const firstTwo = items.slice(0, 2).join(", ");
      const remaining = items.length - 2;
      return remaining > 0 ? `${firstTwo} +${remaining} more` : firstTwo;
    }

    const selectedKey = String(selected);
    const selectedLabel = productById.get(selectedKey) || selectedKey;

    const ordered = Array.from(items);
    const matchIndex = ordered.findIndex(
      (it) =>
        String(it)
          .toLowerCase()
          .includes(String(selectedLabel).toLowerCase()) ||
        String(it).toLowerCase().includes(selectedKey.toLowerCase())
    );

    if (matchIndex > 0) {
      const [match] = ordered.splice(matchIndex, 1);
      ordered.unshift(match);
    }

    const firstTwo = ordered.slice(0, 2).join(", ");
    const remaining = ordered.length - 2;
    return remaining > 0 ? `${firstTwo} +${remaining} more` : firstTwo;
  };

  const fields = [
    {
      key: "order_id",
      columnName: "ID",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_status",
      columnName: "Order status",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "transaction_id",
      columnName: "Transaction ID",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_amount",
      columnName: "Amount ($)",
      type: "text",
      visible: true,
      render: (value) => currencyFormatter(value) ?? "---",
    },
    {
      key: "total_order",
      columnName: "No of Items Ordered",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_success",
      columnName: "No of Items Delivered",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_failed",
      columnName: "No of Items Failed",
      type: "text",
      visible: true,
      disableSorting: true,
      render: (val) => val ?? "---",
    },
    {
      key: "card_number",
      columnName: "Card Number",
      type: "text",
      visible: true,
      disableSorting: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_data",
      columnName: "Order Items",
      type: "text",
      disableSorting: true,
      render: renderOrderItems,
      visible: true,
    },
    {
      key: "user",
      columnName: "User",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "location_name",
      columnName: "Location",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "vm_name",
      columnName: "Device",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "operator_name",
      columnName: "Operator",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_date",
      columnName: "Placed at",
      type: "text",
      visible: true,
      render: (value) => dateFormatter(convertUtcToLocal(value), true) ?? "---",
    },
    {
      key: "data",
      columnName: "E-Receipt",
      type: "text",
      form: false,
      required: false,
      visible: true,
      disableSorting: true,
      render: (_, value) => (
        <>
          <BootstrapTooltip title={"Download E-receipt"}>
            <IconButton
              size="small"
              className="m-1"
              variant="contained"
              disabled={
                !["ITEM_DISPENSING_FAILED_PARTIAL", "SUCCESSFUL"].includes(
                  value.order_status
                )
              }
              onClick={() => {
                downloadPDF(value.order_id);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </BootstrapTooltip>

          <BootstrapTooltip>
            <IconButton
              size="small"
              className="m-1"
              variant="contained"
              disabled={
                !["ITEM_DISPENSING_FAILED_PARTIAL", "SUCCESSFUL"].includes(
                  value.order_status
                )
              }
              style={{ display: "none" }}
              onClick={() => {
                downloadPDF(value.order_id);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </BootstrapTooltip>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchOrder(ordering, page, limit);
  }, []);

  const handleOperator = (val) => {
    updateFilter("operator", val);
    fetchAreas(val);
    fetchVM(val);
    updateFilter("area", { value: "all", label: "All" });
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleArea = (val) => {
    updateFilter("area", val);
    fetchLocations(areaList, val.value, filters.operator);
    fetchVM(filters.operator, val);
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleLocation = (val) => {
    updateFilter("location", val);
    fetchVM(filters.operator, filters.area, val);
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const fetchLocations = async (areaList = [], area_id, selectedOperator) => {
    try {
      setLocationListLoading(true);
      if (area_id && area_id !== "all") {
        const list =
          _.find(areaList, (x) => x.value === area_id)?.locations || [];

        setLocationList([
          { label: "All", value: "all" },
          ..._.map(list, ({ location_id, location_name }) => ({
            label: location_name,
            value: location_id,
          })),
        ]);
      } else {
        if (selectedOperator && selectedOperator?.value !== "all") {
          let newLocation = [];
          const locationData = [
            ..._.map(areaList, ({ locations }) =>
              Array.prototype.push.apply(newLocation, locations)
            ),
          ];
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(newLocation, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        } else {
          const { data } = await window.axiosIns("locations/locations", {
            params: {
              all: true,
              state: "all",
              ordering: "location_name",
              lite: true,
            },
          });
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(data?.results, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLocationListLoading(false);
    }
  };

  const isDateRangeTooLarge = useMemo(() => {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffInMs = end.getTime() - start.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays > 90;
  }, [startDate, endDate]);

  const fetchVM = async (selectedOperator, selectedArea, selectedLocation) => {
    try {
      setVmListLoading(true);
      const params = {
        all: true,
        ordering: "vm_name",
        lite: true,
      };
      if (selectedOperator && selectedOperator.value !== "all")
        params.company_id = selectedOperator.value;
      if (selectedArea && selectedArea.value !== "all")
        params.area_id = selectedArea.value;
      if (selectedLocation && selectedLocation.value !== "all")
        params.location = selectedLocation.value;
      const { data } = await window.axiosIns("device", {
        params,
      });

      setVmList([
        { label: "All", value: "all" },
        ..._.map(data?.data?.results, ({ id, serial_number, vm_name }) => ({
          label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
          value: id,
        })),
      ]);
    } catch (err) {
      console.log("err", err);
      setVmListLoading(false);
    } finally {
      setVmListLoading(false);
    }
  };

  const fetchAreas = async (selectedOperator) => {
    try {
      setAreaListLoading(true);
      const params = { all: true, ordering: "area_name" };
      if (selectedOperator && selectedOperator.value !== "all")
        params.operator_id = selectedOperator.value;
      const { data } = await window.axiosIns("locations/areas", {
        params,
      });

      setAreaList([
        { label: "All", value: "all" },
        ..._.map(data?.results, ({ area_id, area_name, locations }) => ({
          label: area_name,
          value: area_id,
          locations,
        })),
      ]);
      fetchLocations(areaList, null, selectedOperator);
    } catch (err) {
      console.log(err);
      fetchLocations([], null, selectedOperator);
    } finally {
      setAreaListLoading(false);
    }
  };

  const handleSearch = (value) => {
    updateFilter(filterable, { value: value });
    fetchOrder(ordering, 1, limit);
  };

  const buildCommonParams = (extra = {}) => {
    const params = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      // tz: getTimeZoneDifference(),
      _scope: "AND",
      ...extra,
    };

    const map = {
      device_id: filters.vm.value,
      device__location_id: filters.location.value,
      device__location__area_id: filters.area.value,
      device__company_id: filters.operator.value,
      products: filters.product.value,
    };

    Object.entries(map).forEach(([key, val]) => {
      if (val && val !== "all") params[key] = val;
    });

    if (filters.status[0]?.value !== "all") {
      params.status = filters.status?.map((item) => item?.value).join(",");
    }
    if (filters[filterable] && filters[filterable].value) {
      params[filterable] = filters[filterable].value;
    }
    return params;
  };

  const fetchExcel = async () => {
    setLoading(true);
    const params = buildCommonParams();

    try {
      const { data } = await window.axiosIns("orders/export_data", {
        responseType: "blob",
        params: {
          ...params,
          tz: getTimeZoneDifference(),
        },
      });

      if (data) {
        downloadBlob(
          new Blob([data], { type: "application/xlsx" }),
          `Orders-Summary-${moment().format("MM-DD-YYYY")}.xlsx`
        );
      }
    } catch (err) {}
    setLoading(false);
  };

  const fetchPDF = async () => {
    setLoading(true);
    const params = buildCommonParams({
      model: "order",
      type: "pdf",
    });

    try {
      const { data } = await window.axiosIns("orders/export", {
        responseType: "blob",
        params: {
          ...params,
          tz: getTimeZoneDifference(),
        },
      });

      if (data) {
        downloadBlob(
          new Blob([data]),
          `Orders-Summary-${moment().format("MM-DD-YYYY")}.pdf`
        );
      }
    } catch (err) {}
    setLoading(false);
  };

  const fetchOrder = async (order, pageNumber, limit, overrideParams = {}) => {
    setLimit(limit);
    const realPage = pageNumber ? 1 : page + 1;

    const baseParams = {
      ordering: order,
      page: realPage,
      limit,
      history: true,
      latest: true,
    };

    const params = buildCommonParams(baseParams);

    Object.entries(overrideParams).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") {
        delete params[k];
      } else {
        params[k] = v;
      }
    });

    if (filters.status[0]?.value !== "all" && filters.status.length > 0) {
      delete params.latest;
    }

    if (_.isEqual(params, fetchDataParams)) return;

    setFetchDataParams(params);
    setLoader(true);

    try {
      const { data } = await window.axiosIns("orders/custom-orders", {
        params,
      });
      handleRes(data);
      setOrders(data.results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <Grid spacing={1} container style={{ marginBottom: "1rem" }} className={classes.filterBar}>
        <Grid xs={10}>
          <Grid container spacing={1}>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={startDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => {
                  setStartDate(date);
                }}
                label="Start Date"
                maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={endDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => {
                  setEndDate(date);
                }}
                label="End Date"
                minDate={new Date(startDate)}
                maxDate={new Date()}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("product", val);
                  setPage(0);
                }}
                options={productsData}
                value={filters.product}
                loading={false}
                label="Products"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <Autocomplete
                multiple
                style={{ width: "100%" }}
                id="tags-outlined"
                value={filters.status}
                loading={false}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
                options={orderStatusData || []}
                defaultValue={[{ label: "All", value: "all" }]}
                onChange={(event, newValue) => {
                  const allPos = _.findIndex(
                    newValue,
                    (x) => x.value === "all"
                  );
                  if (allPos === 0 && newValue?.length > 1) {
                    const data = _.filter(newValue, (x) => x.value !== "all");
                    updateFilter("status", data);
                  } else if (allPos > 0) {
                    const data = _.filter(newValue, (x) => x.value === "all");
                    updateFilter("status", data);
                  } else {
                    updateFilter("status", newValue);
                  }
                }}
                getOptionLabel={(option) => option?.label || ""}
                renderTags={(tagValue, getTagProps) => {
                  if (tagValue.length < 2) {
                    return tagValue.map((option, index) => {
              const { key, ...tp } = getTagProps({ index });
              return (
                <Chip key={key}
                        {...tp}
                        label={`${option.label.slice(0, 8)}...`}
                      />
              );
            });
                  } else {
                    return (
                      <>
                        <div>
                          {(more ? tagValue : tagValue.slice(0, 1)).map((option, index) => {
              const { key, ...tp } = getTagProps({ index });
              return (
                <Chip key={key}
                                {...tp}
                                label={`${option.label.slice(0, 8)}...`}
                              />
              );
            })}
                          {!more && (
                            <span
                              style={{
                                position: "absolute",
                                fontSize: "13px",
                                top: "20px",
                                cursor: "pointer",
                              }}
                              onClick={() => setMore(!more)}
                            >{`+${tagValue.length - 1} more`}</span>
                          )}
                        </div>
                        {more && (
                          <span
                            style={{
                              position: "static",
                              paddingLeft: "10px",
                              fontSize: "13px",
                              top: "20px",
                              cursor: "pointer",
                            }}
                            onClick={() => setMore(!more)}
                          >
                            show less
                          </span>
                        )}
                      </>
                    );
                  }
                }}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Order Status"
                    slotProps={{ ...params.slotProps, input: { ...params.slotProps.input,
                      endAdornment: (
                        <React.Fragment>
                          {vmListLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps.input.endAdornment}
                        </React.Fragment>
                      ),
                    } }}
                  />
                )}
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleOperator}
                value={filters.operator}
                loading={false}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
                options={operatorsData || []}
                label="Operator"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleArea}
                loading={areaListLoading}
                value={filters.area}
                options={areaList}
                label="Area"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleLocation}
                value={filters.location}
                loading={locationListLoading}
                options={locationList}
                label="Location"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("vm", val);
                  setPage(0);
                }}
                options={vmList}
                loading={vmListLoading}
                value={filters.vm}
                label="Vending Machine"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={2}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ApplyButton
              size="large"
              fullWidth
              onClick={() => fetchOrder(ordering, 1, limit)}
              style={{
                marginBottom: "4px",
                height: 40,
                padding: "0",
                marginRight: "4px",
              }}
              disabled={areaListLoading || locationListLoading || vmListLoading}
            />
            <DownloadButton
              label="EXPORT"
              size="large"
              fullWidth
              Icon={GetAppIcon}
              onPdfDownload={fetchPDF}
              onExcelDownload={fetchExcel}
              disabled={loading || orders?.length <= 0 || isDateRangeTooLarge}
              style={{
                marginBottom: "4px",
                height: 40,
                padding: "0",
                marginRight: "4px",
              }}
            />
          </div>

          <div
          // style={{
          //   display: "flex",
          //   flexDirection: "row",
          //   justifyContent: "space-between",
          // }}
          >
            <SearchBox
              placeholder={"Search " + snakeToTitle(filterable)}
              multiple={false}
              style={{ height: 40 }}
              onChange={(arr) => {
                setFilterable(arr);
              }}
              onInputChange={(value) => {
                updateFilter(filterable, { value });
                // call fetchOrder and *pass the search value explicitly* so fetchOrder
                // doesn't rely on stale filters state
                fetchOrder(ordering, 1, limit, { [filterable]: value });
              }}
              fields={searchFilterFields}
              selectedFields={filterable}
              handleSearch={handleSearch}
            />
          </div>
        </Grid>
      </Grid>

      <TableGenerator
        initialSort={"id"}
        minimal={true}
        nextPage={nextPage}
        previousPage={previousPage}
        searchColumnsFilter={true}
        fields={fields}
        loader={loader}
        data={orders}
        currentPage={page}
        rowOptions={[3, 5, 7, 8, 10, 25, 50, 100]}
        handleSortChange={(ordering) => {
          setOrdering(ordering);
          fetchOrder(ordering, 1, limit);
        }}
        onPageChange={(page, direction) => {
          setPage(page);
          if (direction === "next") {
            changePage(nextPage);
          } else if (direction === "back") {
            changePage(previousPage);
          }
        }}
        backendPagination={true}
        onRowPerPageChange={(rows) => {
          setLimit(rows);
          setPage(0);
          fetchOrder(ordering, 1, rows);
        }}
        onChangePage={(page) => console.log(page)}
        selectedRecords={modulesSelected}
        collapsible={true}
        collapsibleHeader={"Order Details"}
        collapsibleFields={collapsibleTableFields}
        collapsibleFieldKey={"order_items"}
        collapsibleHeader2={"Order History"}
        collapsibleFields2={collapsibleTableFields2}
        collapsibleFieldKey2={"history"}
        collapsibleFieldKey3={"order_summery"}
        collapsibleHeader3={"Order Summary"}
        collapsibleFields3={collapsibleTableFields3}
        rowOnePage={25}
        onChangeSelected={(modulesSelected) =>
          setModulesSelected(modulesSelected)
        }
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.userReducer.current_user,
    appLogo: state.userReducer.appLogo,
  };
};

export default connect(mapStateToProps)(Orders);
